import { useEffect, useRef } from 'react';
import L from 'leaflet';

/**
 * Renders CV matching candidates on the Leaflet map as circle markers.
 * Manages its own LayerGroup separate from drawnItemsRef.
 *
 * @param {Object} params
 * @param {Object} params.mapRef - React ref to the Leaflet map instance
 * @param {Array} params.candidates - Array of candidate objects with { x, y, w, h, confidence, scale, center, status }
 * @param {string|null} params.matchItemId - The active match item ID; null means no candidates active
 * @param {Function} params.onCandidateClick - Callback invoked with candidate index on click
 */
export default function useCandidateRendering({ mapRef, candidates, matchItemId, onCandidateClick }) {
  const layerGroupRef = useRef(null);

  // Create and manage the LayerGroup lifecycle
  useEffect(() => {
    const map = mapRef?.current;
    if (!map) return;

    const layerGroup = L.layerGroup().addTo(map);
    layerGroupRef.current = layerGroup;

    return () => {
      layerGroup.remove();
      layerGroupRef.current = null;
    };
  }, [mapRef]);

  // Render candidates when they change
  useEffect(() => {
    const layerGroup = layerGroupRef.current;
    if (!layerGroup) return;

    // Clear existing markers
    layerGroup.clearLayers();

    // Don't render if no active match item
    if (!matchItemId) return;

    // Don't render if no candidates
    if (!candidates || candidates.length === 0) return;

    candidates.forEach((candidate, idx) => {
      const { confidence, center, status } = candidate;

      // Skip rejected candidates
      if (status === 'rejected') return;

      let style = {};

      if (status === 'accepted') {
        style = {
          radius: 12,
          color: '#4caf50',
          fillColor: '#4caf50',
          fillOpacity: 0.9,
          opacity: 0.9,
          weight: 2,
          dashArray: null,
          fill: true,
        };
      } else if (status === 'pending' && confidence >= 0.85) {
        style = {
          radius: 10,
          color: '#4caf50',
          fillColor: '#4caf50',
          fillOpacity: 0.8,
          opacity: 0.8,
          weight: 2,
          dashArray: null,
          fill: true,
        };
      } else if (status === 'pending') {
        style = {
          radius: 10,
          color: '#ff9800',
          fillColor: 'transparent',
          fillOpacity: 0,
          opacity: 0.7,
          weight: 2,
          dashArray: '4 4',
          fill: false,
        };
      }

      // In CRS.Simple: lat = pixelY, lng = pixelX
      const latlng = [center.y, center.x];

      const marker = L.circleMarker(latlng, style);

      // Bind tooltip showing confidence percentage
      marker.bindTooltip(`${(confidence * 100).toFixed(0)}%`, {
        permanent: false,
      });

      // Bind click handler
      marker.on('click', () => {
        if (onCandidateClick) {
          onCandidateClick(idx);
        }
      });

      marker.addTo(layerGroup);
    });
  }, [candidates, matchItemId, onCandidateClick]);
}
